import { Gesture } from "@-/com.npmjs.react-native-gesture-handler";
import { Platform } from "@-/com.npmjs.react-native";
import { useSharedValue } from "@-/com.npmjs.react-native-reanimated";
import { useMemo } from "react";

/**
 * Whether a finger is down on a button, tracked on the UI thread (`pressed`: 1 while down), so the
 * press shows in the very frame the finger lands even while the JS thread is busy, for example
 * mounting the card a swipe just uncovered. The button keeps handling the press itself: `gesture`
 * only observes touches and never activates, so it cannot take the touch from the button (a
 * gesture handler cancels the JS responder only once it activates). `onPressIn` / `onPressOut`
 * also drive `pressed`, which is all web has.
 */
export function useLearnCardButtonPress(isEnabled: boolean) {
  const pressed = useSharedValue(0);
  const gesture = useMemo(
    () =>
      Gesture.Manual()
        .enabled(isEnabled && Platform.OS !== "web")
        .shouldCancelWhenOutside(true)
        .onBegin(() => {
          "worklet";
          pressed.set(1);
        })
        .onTouchesDown(() => {
          "worklet";
          pressed.set(1);
        })
        .onTouchesUp(() => {
          "worklet";
          pressed.set(0);
        })
        .onTouchesCancelled(() => {
          "worklet";
          pressed.set(0);
        })
        .onFinalize(() => {
          "worklet";
          pressed.set(0);
        }),
    [isEnabled, pressed],
  );
  const handlers = useMemo(
    () => ({
      onPressIn: () => {
        if (isEnabled) {
          pressed.set(1);
        }
      },
      onPressOut: () => {
        pressed.set(0);
      },
    }),
    [isEnabled, pressed],
  );
  return { gesture, handlers, pressed };
}
